import { TicketIssueStatus } from "@/constants/accreditation";
import { formatDateWithTime } from "@/utils/date";
import { CheckCircleIcon } from "lucide-react";
import { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import axios from "axios";
import { BASE_URL } from "@/dashboardQueries";
import toast from "react-hot-toast";
import { invalidateAccreditationGroups } from "@/utils/accreditation";

export const TicketBadge = ({
  groupId,
  individualId,
  ticket,
}: {
  groupId: number;
  individualId?: number;
  ticket: any;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const queryClient = useQueryClient();

  const invalidateGroup = () => {
    queryClient.invalidateQueries(["group", groupId.toString()]);
    queryClient.invalidateQueries(["groups", groupId.toString(), "", 1]);
    queryClient.invalidateQueries(["individuals", groupId.toString(), "", 1]);
    if (individualId) {
      queryClient.invalidateQueries(["individual", individualId.toString()]);
    }
  };

  const checkInMutation = useMutation(
    async () => {
      const response = await axios.post(
        `${BASE_URL}/dashboard/attendees/${ticket.uuid}/check-in`
      );
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success("Checked in successfully");
        invalidateGroup();
      },
    }
  );

  const checkOutMutation = useMutation(
    async () => {
      const response = await axios.post(
        `${BASE_URL}/dashboard/attendees/${ticket.uuid}/check-in?check_out=true`
      );
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success("Checked out successfully");
        invalidateGroup();
      },
    }
  );

  const handleCheckIn = (e: React.MouseEvent) => {
    e.stopPropagation();
    checkInMutation.mutate();
  };

  const handleCheckOut = (e: React.MouseEvent) => {
    e.stopPropagation();
    checkOutMutation.mutate();
  };

  const isLoading = checkInMutation.isLoading || checkOutMutation.isLoading;

  return (
    <span
      className={`cursor-pointer has-tooltip relative mr-1.5 mb-1 inline-flex items-center rounded-md px-2 py-1 text-xs font-medium text-gray-400 ring-1 ring-inset transition-all duration-200 ease-out ${
        ticket.checkedIn && ticket.checkedInAt
          ? "bg-green-500/10 text-green-500 ring-green-500/20"
          : "bg-gray-400/10 text-gray-400 ring-gray-400/20"
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(e) => {
        e.stopPropagation();
        if (ticket.checkedIn) {
          handleCheckOut(e);
        } else {
          handleCheckIn(e);
        }
      }}
    >
      {ticket.order && ticket.status === TicketIssueStatus.PENDING && (
        <svg
          id="ticket-pending-icon"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-clock-alert-icon lucide-clock-alert w-3.5 h-3.5 mr-1"
        >
          <path d="M12 6v6l4 2" />
          <path d="M16 21.16a10 10 0 1 1 5-13.516" />
          <path d="M20 11.5v6" />
          <path d="M20 21.5h.01" />
        </svg>
      )}

      {ticket.checkedIn && ticket.checkedInAt && (
        <>
          <CheckCircleIcon className="h-3 w-3 text-green-500 mr-2" />
          <div className="tooltip rounded shadow-lg ml-2 px-2 py-1 bg-dark-secondary top-0 -mt-8 left-0 -translate-x-1/4 text-nowrap text-xs text-gray-400 border border-white/10 font-light">
            Checked in at {formatDateWithTime(ticket.checkedInAt)}
          </div>
        </>
      )}

      {isLoading ? (
        <span className="text-nowrap absolute top-0 left-0 w-full h-full flex items-center justify-center bg-black/60 text-white font-medium rounded-md cursor-default">
          {checkInMutation.isLoading ? "Checking in..." : "Checking out..."}
        </span>
      ) : (
        <span
          className={`text-nowrap transition-all duration-300 ease-out transform ${
            isHovered ? "opacity-100 scale-110" : "opacity-0 scale-90"
          } absolute top-0 left-0 w-full h-full flex items-center justify-center bg-black/60 text-white font-medium rounded-md cursor-pointer`}
        >
          {ticket.checkedIn ? "Check out?" : "Check in"}
        </span>
      )}

      <span
        className={`transition-all duration-300 ease-out ${
          isHovered && !isLoading ? "opacity-0" : "opacity-100"
        }`}
      >
        {ticket.allocation.ticket.name}
      </span>
    </span>
  );
};
